import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';
import { WorldCupGameItem } from './use-world-cup-games';

export interface WorldCupGroupResponse {
  success: boolean;
  data: WorldCupGameItem[];
  group: string | null;
  message?: string;
}

export interface UseWorldCupGroupOptions {
  eventSlug?: string | null;
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupGroupResponse, Error>;
}

export async function fetchWorldCupGroup(eventSlug: string) {
  const searchParams = new URLSearchParams({
    event_slug: eventSlug,
  });

  const response = await customFetch(`/api/world-cup/game/group?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch World Cup group games');
  }

  const result = (await response.json()) as WorldCupGroupResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup group games');
  }

  return result;
}

export function useWorldCupGameGroup(options: UseWorldCupGroupOptions = {}) {
  const eventSlug = options.eventSlug?.trim();
  const enabled = options.enabled ?? true;
  const swrKey = enabled && eventSlug ? ['world-cup-group', eventSlug] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<WorldCupGroupResponse, Error>(
    swrKey,
    () => fetchWorldCupGroup(eventSlug!),
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    group: data?.group ?? null,
    games: data?.data ?? [],
    list: data?.data ?? [],
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupGameGroup;
